import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { CodeService } from '@app/shared/common/code/code.service';
import { ProfessionConfigurationService } from '@app/shared/services/profession-configuration.service';
import { AppConsts } from '@shared/AppConsts';
import { SelectItem } from 'primeng/api';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { delay, map, switchMap, tap } from 'rxjs/operators';
import { ApplicationServiceProxy, ApplicationUploadFilesInput, CreateLiaTypeInput, CreateLiaTypeOutput, CreateOrUpdateLiaAcademicTitleInput, CreateOrUpdateLiaAcademicTitleOutput, CreateOrUpdateLiaContactDuringEmploymentInput, CreateOrUpdateLiaContactInput, CreateOrUpdateLiaDiplomaInput, CreateOrUpdateLiaEmploymentBInput, CreateOrUpdateLiaInvoiceAddressInput, CreateOrUpdateLiaLanguageSkillBInput, CreateOrUpdateLiaLicenceInAnotherCantonInput, CreateOrUpdateLiaPersonExtendedInput, CreateOrUpdateLiaPersonExtendedOutput, CreateOrUpdateLiaPersonOutputDto, CreateOrUpdateLiaPersonWithRegisterCheckInput, CreateOrUpdateLiaPostgraduateTitleInput, CreateOrUpdateLiaPostgraduateTitleOutput, CreateOrUpdateLiaPostgraduateTitlePrivateLawInput, CreateOrUpdateLiaPostgraduateTitlePrivateLawOutput, CreateOrUpdateLiaPreviousEmploymentInput, CreateOrUpdateLiaRestrictionBInput, ExternalSystemEnum, GeneralDocumentGroupDto, GeneralFileDto, GeneralFileInfoDto, GetLiaOutput, LiaAcademicTitleDto, LiaAcademicTitleServiceProxy, LiaContactDto, LiaContactDuringEmploymentDto, LiaContactDuringEmploymentServiceProxy, LiaContactServiceProxy, LiaDiplomaDto, LiaDiplomaServiceProxy, LiaDocumentServiceProxy, LiaDocumentsOutput, LiaDto, LiaEmploymentBDto, LiaEmploymentBRecordDto, LiaEmploymentBServiceProxy, LiaGuaranteeServiceProxy, LiaInvoiceAddressDto, LiaInvoiceAddressServiceProxy, LiaLanguageSkillBDto, LiaLanguageSkillBServiceProxy, LiaLicenceInAnotherCantonCacheDto, LiaLicenceInAnotherCantonDto, LiaLicenceInAnotherCantonServiceProxy, LiaPersonDto, LiaPersonServiceProxy, LiaPostgraduateTitleDto, LiaPostgraduateTitlePrivateLawDto, LiaPostgraduateTitlePrivateLawServiceProxy, LiaPostgraduateTitleServiceProxy, LiaPreviousEmploymentDto, LiaPreviousEmploymentServiceProxy, LiaProfessionalResponsibilityServiceProxy, LiaRestrictionBDto, LiaRestrictionBServiceProxy, LiaStatus, LiaSummaryDto, LiaTypeServiceProxy, ProfessionalLicenceDto, ProfessionRegisterAffiliationServiceProxy, ProfessionRequirementType } from './../../../shared/service-proxies/service-proxies';

export class LiaInvoiceAddressDetail {
    name!: string | undefined;
    street!: string;
    streetNr!: string | undefined;
    postCode!: string;
    city!: string;
    email!: string;
    countryId!: string | undefined;

    constructor(data?: LiaInvoiceAddressDetail) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property)) {
                    (<any>this)[property] = (<any>data)[property];
                }
            }
        }
    }
}

@Injectable()
export class LiaService extends ApplicationService {

    lia: LiaDto;
    liaResultDocuments: GeneralFileInfoDto[];
    healthRegisterSystem: ExternalSystemEnum;
    onlyPrivateAddressAllowed = false;

    private liasForPersonSubject = new ReplaySubject<LiaSummaryDto[]>(1);
    readonly liasForPerson$ = this.liasForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private appService: ApplicationServiceProxy,
        codeService: CodeService,
        private professionConfigurationService: ProfessionConfigurationService,
        private liaTypeService: LiaTypeServiceProxy,
        private liaPersonService: LiaPersonServiceProxy,
        private liaContactService: LiaContactServiceProxy,
        private liaContactDuringEmploymentService: LiaContactDuringEmploymentServiceProxy,
        private liaRestrictionBService: LiaRestrictionBServiceProxy,
        private liaEmploymentBService: LiaEmploymentBServiceProxy,
        private liaPreviousEmploymentService: LiaPreviousEmploymentServiceProxy,
        private liaLanguageSkillBService: LiaLanguageSkillBServiceProxy,
        private liaInvoiceAddressService: LiaInvoiceAddressServiceProxy,
        private liaDocumentService: LiaDocumentServiceProxy,
        private liaDiplomaService: LiaDiplomaServiceProxy,
        private liaAcademicTitleService: LiaAcademicTitleServiceProxy,
        private liaGuaranteeService: LiaGuaranteeServiceProxy,
        private liaPostgraduateTitlePrivateLawService: LiaPostgraduateTitlePrivateLawServiceProxy,
        private liaPostgraduateTitleService: LiaPostgraduateTitleServiceProxy,
        private liaLicenceInAnotherCantonService: LiaLicenceInAnotherCantonServiceProxy,
        private liaProfessionalResponsibilityService: LiaProfessionalResponsibilityServiceProxy,
        private professionRegisterService: ProfessionRegisterAffiliationServiceProxy
    ) {
        super(injector, codeService, appService);
    }

    loadLiasForPerson(gln: string): Observable<LiaSummaryDto[]> {
        if (gln) {
            return this.liaTypeService.getLiasForPerson(gln).pipe(
                tap(lias => this.liasForPersonSubject.next(lias))
            );
        } else {
            this.liasForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaTypeService.getExternalRegisterByProfessionId(professionId);
    }

    getFailedLiaForProfessionAndGln(caseId: string): Observable<GetLiaOutput> {
        return this.liaTypeService.getFailedLiaForProfessionAndGln(caseId);
    }

    getActiveProfessions(): Observable<string[]> {
        return this.liaTypeService.getActiveProfessions();
    }

    isExtendedLiaPersonEnabled(): Observable<boolean> {
        return this.liaTypeService.isExtendedLiaPersonEnabled(this.profession);
    }

    createLia(input: CreateLiaTypeInput): Observable<CreateLiaTypeOutput> {
        return this.liaTypeService.createLiaType(input).pipe(tap((output: CreateLiaTypeOutput) => {
            this.lia = null;
        }));
    }

    deleteLia(): Observable<void> {
        return this.appService.deleteApplication(this.lia.application.caseId);
    }

    getLia(caseId: string): Observable<GetLiaOutput> {
        this.lia = null;
        return this.getApplication(caseId).pipe(
            switchMap(() => this.liaTypeService.getLia(caseId).pipe(
                tap((output: GetLiaOutput) => {
                    this.lia = output.lia;

                    return forkJoin([
                        this.professionConfigurationService.getRequirementByProfessionAndType(this.lia.professionId, ProfessionRequirementType.OnlyPrivateAddressAllowed),
                        this.professionRegisterService.getAll(),
                        of(output.lia)
                    ]).subscribe(([result, affiliations, lia]) => {
                        this.onlyPrivateAddressAllowed = result.value === this.codes.yesNo.yes;
                        this.healthRegisterSystem = affiliations.find(a => a.id === lia.professionId).externalSystem;
                    });
                })
            )
        ));
    }

    getMyLias(): Observable<LiaDto[]> {
        return this.liaTypeService.getMyLias();
    }

    isInLiaStatus(expectedStatus: LiaStatus): boolean {
        return this.lia.currentStep === expectedStatus;
    }

    getProfessionalLicencePersonIdForApplication(): Observable<ProfessionalLicenceDto> {
        return this.liaTypeService.getProfessionalLicenceForLia(this._caseId);
    }

    getEvaluationComments(): Observable<string> {
        return this.liaTypeService.getEvaluationComments(this._caseId);
    }

    releaseLia(): Observable<void> {
        return this.liaTypeService.releaseLia(this.lia.application.caseId);
    }

    getNextStatus(status: LiaStatus): Observable<LiaStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.liaTypeService.getNextStatus(this._caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === LiaStatus.Release) {
                return LiaStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: LiaStatus): string {
        return this.getNextStep(this.getUrlForStep(nextStep));
    }

    getFile(step: LiaStatus, id: number): Observable<GeneralFileDto> {
        return this.liaDocumentService.getFile(this._caseId, step, id);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaDocumentService.uploadFiles(input);
    }

    deleteFile(step: LiaStatus, id: number): Observable<void> {
        return this.liaDocumentService.deleteFile(this._caseId, step, id);
    }

    get caseId(): string {
        return this.lia.application.caseId;
    }

    get profession(): string {
        return this.lia?.professionId;
    }

    get medRegId(): number {
        return this.lia.liaPerson.medRegId;
    }

    get hasMedRegId(): boolean {
        return this.lia.liaPerson.medRegId != null;
    }

    get psyRegId(): number {
        return this.lia.liaPerson.psyRegId;
    }

    get hasPsyRegId(): boolean {
        return this.lia.liaPerson.psyRegId != null;
    }

    get naregId(): number {
        return this.lia.liaPerson.naregId;
    }

    get hasNaregId(): boolean {
        return this.lia.liaPerson.naregId != null;
    }

    get healthRegisterPersonId(): number {

        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                return this.lia.liaPerson.medRegId;
            case ExternalSystemEnum.PsyReg:
                return this.lia.liaPerson.psyRegId;
            case ExternalSystemEnum.Nareg:
                return this.lia.liaPerson.naregId;
            default:
                return null;
        }
    }

    get healthRegisterSystemId(): ExternalSystemEnum {
        return this.healthRegisterSystem;
    }


    getPerson(): Observable<LiaPersonDto> {
        return of(this.lia.liaPerson).pipe(delay(1));
    }

    setLiaPersonStepToReview() {
        this.setStepToReview(LiaStatus.Person);
    }

    createOrUpdateLiaPersonWithRegisterCheck(input: CreateOrUpdateLiaPersonWithRegisterCheckInput): Observable<CreateOrUpdateLiaPersonOutputDto> {
        this.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.createOrUpdateLiaPersonWithRegisterCheck(input).pipe(tap((output: CreateOrUpdateLiaPersonOutputDto) => {
            this.lia.liaPerson = output.liaPerson;
        }));
    }

    createOrUpdateLiaPersonExtended(input: CreateOrUpdateLiaPersonExtendedInput): Observable<CreateOrUpdateLiaPersonExtendedOutput> {
        this.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.createOrUpdateLiaPersonExtended(input).pipe(tap((output: CreateOrUpdateLiaPersonExtendedOutput) => {
            this.lia.liaPerson = output.liaPerson;
            this.lia.currentStep = output.status;
        }));
    }

    acceptRegisterCheck(): Observable<LiaStatus> {
        this.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.acceptRegisterCheck(this.caseId).pipe(tap((status: LiaStatus) => {
            this.lia.currentStep = status;
        }));
    }

    getLiaPersonFiles(forceRefresh?: boolean, hasLicenceInAnotherCanton?: boolean, nationality?: string, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaPersonService.getLiaPersonDocuments(caseId, LiaStatus.Person, hasLicenceInAnotherCanton, nationality).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaPerson.personDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaPerson.personDocuments);
    }

    uploadLiaPersonFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaPersonService.uploadFiles(input);
    }


    get hasLicenceInOtherCanton(): boolean {
        return this.lia.liaPerson.hasLicenceInAnotherCantonId == this.codes.yesNo.yes;
    }


    getContact(): Observable<LiaContactDto> {
        return of(this.lia.liaContact).pipe(delay(0));
    }

    createOrUpdateLiaContact(input: CreateOrUpdateLiaContactInput): Observable<void> {
        this.setStepToReview(LiaStatus.Contact);
        return this.liaContactService.createOrUpdateLiaContact(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaContact = input.liaContact;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    getContactDuringEmployment(): Observable<LiaContactDuringEmploymentDto> {
        return of(this.lia.liaContactDuringEmployment).pipe(delay(0));
    }

    createOrUpdateLiaContactDuringEmployment(input: CreateOrUpdateLiaContactDuringEmploymentInput): Observable<void> {
        this.setStepToReview(LiaStatus.ContactDuringEmployment);
        return this.liaContactDuringEmploymentService.createOrUpdateLiaContactDuringEmployment(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaContactDuringEmployment = input.liaContactDuringEmployment;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    getDiploma(): Observable<LiaDiplomaDto> {
        return of(this.lia.liaDiploma);
    }

    createOrUpdateLiaDiploma(input: CreateOrUpdateLiaDiplomaInput): Observable<void> {
        this.setStepToReview(LiaStatus.Diploma);
        return this.liaDiplomaService.createOrUpdateLiaDiploma(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaDiploma = input.liaDiploma;
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    getAcademicTitles(): Observable<LiaAcademicTitleDto[]> {
        return of(this.lia.liaAcademicTitles.titles);
    }

    createBlankLiaAcademicTitle(): Observable<string> {
        return this.liaAcademicTitleService.createBlankLiaAcademicTitle(this.caseId);
    }

    deleteLiaAcademicTitle(guid: string): Observable<void> {
        return this.liaAcademicTitleService.deleteLiaAcademicTitle(this.caseId, guid);
    }

    createOrUpdateLiaAcademicTitles(input: CreateOrUpdateLiaAcademicTitleInput): Observable<void> {
        this.setStepToReview(LiaStatus.AcademicTitle);
        return this.liaAcademicTitleService.createOrUpdateLiaAcademicTitle(input).pipe(tap((output: CreateOrUpdateLiaAcademicTitleOutput) => {
            this.lia.liaAcademicTitles.titles = output.liaAcademicTitles;
            this.lia.currentStep = output.status;
        })).pipe(map(() => null));
    }

    getLiaAcademicTitleFiles(forceRefresh?: boolean, caseId = this._caseId) : Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaAcademicTitleService.getLiaAcademicTitleFiles(caseId, LiaStatus.AcademicTitle).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaAcademicTitles.documents = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaAcademicTitles.documents);
    }

    uploadLiaAcademicTitleFiles(liaAcademicTitleId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaAcademicTitleService.uploadFiles(liaAcademicTitleId, input);
    }

    hasAtLeastOneEmployment(): boolean {
        if (this.lia.liaEmploymentB?.employmentRecords?.length > 0 || this.lia.liaEmploymentB?.hasMobileEmployment) {
            return true;
        }
        return false;
    }

    hasEmploymentB(): boolean {
        return this.lia.liaEmploymentB?.employmentRecords?.length > 0;
    }

    getLiaEmploymentB(): Observable<LiaEmploymentBDto> {
        return of(this.lia.liaEmploymentB);
    }

    createOrUpdateLiaEmploymentB(input: CreateOrUpdateLiaEmploymentBInput): Observable<void> {
        this.setStepToReview(LiaStatus.EmploymentB);
        return this.liaEmploymentBService.createOrUpdateLiaEmploymentsB(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaEmploymentB = input.liaEmploymentB;
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    createBlankLiaEmploymentBRecord(): Observable<string> {
        return this.liaEmploymentBService.createBlankLiaEmploymentBRecord(this.caseId);
    }

    deleteLiaEmploymentBRecord(guid: string): Observable<void> {
        return this.liaEmploymentBService.deleteLiaEmploymentBRecord(this.caseId, guid);
    }

    getLiaEmploymentBFiles(forceRefresh?: boolean, caseId = this._caseId) : Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaEmploymentBService.getLiaEmploymentBFiles(caseId, LiaStatus.EmploymentB).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaEmploymentB.employmentDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaEmploymentB.employmentDocuments);
    }

    uploadLiaEmploymentBFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaEmploymentBService.uploadFiles(input);
    }

    getLiaEmploymentBRecordFiles(forceRefresh?: boolean, caseId = this._caseId, typeOfEmploymentId: string = undefined, isMobileEmploymentId: string = undefined, requestsVaccinationLicenceId: string = undefined) : Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaEmploymentBService.getLiaEmploymentBRecordFiles(caseId, typeOfEmploymentId, isMobileEmploymentId, requestsVaccinationLicenceId).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaEmploymentB.employmentRecordDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaEmploymentB.employmentRecordDocuments);
    }

    uploadLiaEmploymentBRecordFiles(recordId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaEmploymentBService.uploadRecordFiles(recordId, input);
    }

    getLiaGuaranteeFiles(forceRefresh?: boolean, caseId = this._caseId) : Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaGuaranteeService.getLiaGuaranteeFiles(caseId, LiaStatus.Guarantee).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaGuaranteeOutput.documents = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaGuaranteeOutput.documents);
    }

    liaGuaranteeProceed(): Observable<void> {
        this.setStepToReview(LiaStatus.Guarantee);
        return this.liaGuaranteeService.proceed(this.caseId).pipe(tap((status: LiaStatus) => {
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    uploadLiaGuaranteeFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaGuaranteeService.uploadFiles(input);
    }

    uploadLiaProfessionalResponsibilityFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaProfessionalResponsibilityService.uploadFiles(input);
    }

    getLiaProfessionalResponsibilityFiles(forceRefresh?: boolean, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaProfessionalResponsibilityService.getLiaProfessionalResponsibilityFiles(caseId, LiaStatus.ProfessionalResponsibility).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaProfessionalResponsibilityOutput.documents = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaProfessionalResponsibilityOutput.documents);
    }

    liaProfessionalResponsibilityProceed(): Observable<void> {
        this.setStepToReview(LiaStatus.ProfessionalResponsibility);
        return this.liaProfessionalResponsibilityService.proceed(this.caseId).pipe(tap((status: LiaStatus) => {
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }


    getLiaPostgraduateTitles(): Observable<LiaPostgraduateTitleDto[]> {
        return of(this.lia.liaPostgraduateTitleOutput.liaPostgraduateTitles);
    }

    createOrUpdateLiaPostgraduateTitles(input: CreateOrUpdateLiaPostgraduateTitleInput): Observable<void> {
        this.setStepToReview(LiaStatus.PostgraduateTitle);
        return this.liaPostgraduateTitleService.createOrUpdateLiaPostgraduateTitles(input).pipe(tap((output: CreateOrUpdateLiaPostgraduateTitleOutput) => {
            this.lia.liaPostgraduateTitleOutput = output;
            this.lia.currentStep = output.status;
        })).pipe(map(() => null));
    }


    getLiaPostgraduateTitlePrivateLaws(): Observable<LiaPostgraduateTitlePrivateLawDto[]> {
        return of(this.lia.liaPostgraduateTitlePrivateLawOutput.liaPostgraduateTitlePrivateLaws);
    }

    createBlankLiaPostgraduateTitlePrivateLaw(): Observable<string> {
        return this.liaPostgraduateTitlePrivateLawService.createBlankLiaPostgraduateTitlePrivateLaw(this.caseId);
    }

    createOrUpdateLiaPostgraduateTitlePrivateLaw(input: CreateOrUpdateLiaPostgraduateTitlePrivateLawInput): Observable<void> {
        this.setStepToReview(LiaStatus.PostgraduateTitlePrivateLaw);
        return this.liaPostgraduateTitlePrivateLawService.createOrUpdateLiaPostgraduateTitlePrivateLaw(input).pipe(tap((output: CreateOrUpdateLiaPostgraduateTitlePrivateLawOutput) => {
            this.lia.liaPostgraduateTitlePrivateLawOutput.liaPostgraduateTitlePrivateLaws = output.liaPostgraduateTitlePrivateLaws;
            this.lia.currentStep = output.status;
        })).pipe(map(() => null));
    }

    deleteLiaPostgraduateTitlePrivateLaw(guid: string): Observable<void> {
        return this.liaPostgraduateTitlePrivateLawService.deletePrivateLiaPostgraduateTitlePrivateLaw(this.caseId, guid);
    }

    getLiaPostgraduateTitlePrivateLawRecordFiles(titleId: string, caseId = this._caseId) : Observable<GeneralDocumentGroupDto[]> {
        return this.liaPostgraduateTitlePrivateLawService.getFiles(caseId, titleId).pipe(tap((output: LiaDocumentsOutput) => {
            this.lia.liaPostgraduateTitlePrivateLawOutput.postgraduateTitlePrivateLawDocuments = output.liaDocuments;
        })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
    }

    uploadLiaPostgraduateTitlePrivateLawRecordFiles(recordId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaPostgraduateTitlePrivateLawService.uploadFiles(recordId, input);
    }

    getLiaPostgraduateTitlePrivateLawRecordFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaPostgraduateTitlePrivateLawService.getFile(this._caseId, LiaStatus.PostgraduateTitlePrivateLaw, fileId)
    }

    deleteLiaPostgraduateTitlePrivateLawRecordFile(fileId: number): Observable<void> {
        return this.liaPostgraduateTitlePrivateLawService.deleteFile(this._caseId, LiaStatus.PostgraduateTitlePrivateLaw, fileId);
    }

    createOrUpdateLiaRestrictionB(input: CreateOrUpdateLiaRestrictionBInput) : Observable<void> {
        this.setStepToReview(LiaStatus.RestrictionB);
        return this.liaRestrictionBService.createOrUpdateLiaRestrictionB(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaRestrictionB = input.liaRestrictionB;
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    getLiaRestrictionB(): Observable<LiaRestrictionBDto>
    {
        return of(this.lia.liaRestrictionB);
    }

    getRestrictionBFiles(forceRefresh?: boolean, areProceedingsDocumentsRequired: boolean = false, areForeignDocumentsRequired: boolean = false, hasLicenceInAnotherCanton: boolean | undefined = undefined, wasSelfEmployedInAnotherCanton: boolean | undefined = undefined, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaRestrictionBService.getLiaRestrictionBDocuments(caseId, LiaStatus.RestrictionB, areProceedingsDocumentsRequired, areForeignDocumentsRequired, hasLicenceInAnotherCanton, wasSelfEmployedInAnotherCanton).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaRestrictionB.restrictionBDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaRestrictionB.restrictionBDocuments);
    }

    uploadRestrictionBFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaRestrictionBService.uploadFiles(input);
    }

    getLiaPreviousEmployment(): Observable<LiaPreviousEmploymentDto> {
        return of(this.lia.liaPreviousEmployment);
    }

    createOrUpdateLiaPreviousEmployment(input: CreateOrUpdateLiaPreviousEmploymentInput): Observable<void> {
        this.setStepToReview(LiaStatus.PreviousEmployment);
        return this.liaPreviousEmploymentService.createOrUpdateLiaPreviousEmployment(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaPreviousEmployment = input.liaPreviousEmployment;
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    deleteLiaPreviousEmploymentRecord(guid: string): Observable<void> {
        return this.liaPreviousEmploymentService.deleteLiaPreviousEmploymentRecord(this.caseId, guid);
    }

    createBlankLiaPreviousEmploymentRecord(): Observable<string> {
        return this.liaPreviousEmploymentService.createBlankLiaPreviousEmploymentRecord(this.caseId);
    }

    getLiaPreviousEmploymentRecordFiles(forceRefresh?: boolean, caseId = this._caseId) : Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaPreviousEmploymentService.getLiaPreviousEmploymentRecordFiles(caseId, LiaStatus.PreviousEmployment).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaPreviousEmployment.previousEmploymentRecordDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaPreviousEmployment.previousEmploymentRecordDocuments);
    }

    uploadLiaPreviousEmploymentRecordFile(recordId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaPreviousEmploymentService.uploadRecordFiles(recordId, input);
    }

    createOrUpdateLiaLanguageSkillB(input: CreateOrUpdateLiaLanguageSkillBInput): Observable<void> {
        this.setStepToReview(LiaStatus.LanguageSkillB);
        return this.liaLanguageSkillBService.createOrUpdateLiaLanguageSkillB(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaLanguageSkillB = input.liaLanguageSkillB;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }

    getLiaLanguageSkillB(): Observable<LiaLanguageSkillBDto> {
        return of(this.lia.liaLanguageSkillB);
    }

    getLanguageSkillBFiles(forceRefresh?: boolean, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaLanguageSkillBService.getLiaLanguageSkillBDocuments(caseId, LiaStatus.LanguageSkillB).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaLanguageSkillB.languageSkillBDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaLanguageSkillB.languageSkillBDocuments);
    }

    uploadLanguageSkillBFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaLanguageSkillBService.uploadFiles(input);
    }

    getAndUpdateLicenceInAnotherCantonStatus(): Observable<LiaLicenceInAnotherCantonCacheDto[]> {
        return this.liaRestrictionBService.getAndUpdateLicenceInAnotherCantonStatus(this.caseId);
    }

    createOrUpdateLicenceInAnotherCanton(input: CreateOrUpdateLiaLicenceInAnotherCantonInput): Observable<void> {
        this.setStepToReview(LiaStatus.LicenceInAnotherCanton);
        return this.liaLicenceInAnotherCantonService.createOrUpdateLiaLicenceInAnotherCanton(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaLicenceInAnotherCanton = input.liaLicenceInAnotherCanton;
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    getLicenceInAnotherCanton(): Observable<LiaLicenceInAnotherCantonDto> {
        return of(this.lia.liaLicenceInAnotherCanton);
    }

    getLiaLicenceInAnotherCantonFiles(hasLicenceId: string, hasAnyUnsuccessfulLicenceApplicationId: string, hasRegistryEntry: boolean, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.liaLicenceInAnotherCantonService.getDocuments(caseId, LiaStatus.LicenceInAnotherCanton, hasLicenceId, hasAnyUnsuccessfulLicenceApplicationId, hasRegistryEntry).pipe(tap((output: LiaDocumentsOutput) => {
            this.lia.liaPerson.personDocuments = output.liaDocuments;
        })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
    }

    getInvoiceAddress(): Observable<LiaInvoiceAddressDto> {
        return of(this.lia.liaInvoiceAddress).pipe(delay(1));
    }

    getInvoiceAddressDetail(): Observable<LiaInvoiceAddressDetail> {
        let detail: LiaInvoiceAddressDetail;
        if (this.lia.liaInvoiceAddress.typeOfAddressId == this.codes.address.private) {
            let fullName = this.lia.liaContact.titleId !== null ? this.l(this.lia.liaContact.titleId) + ' ' + this.lia.application.name : this.lia.application.name;
            detail = new LiaInvoiceAddressDetail({
                name: fullName,
                street: this.lia.liaContact.street,
                streetNr: this.lia.liaContact.streetNr,
                postCode: this.lia.liaContact.postCode,
                city: this.lia.liaContact.city,
                email: this.lia.liaContact.email,
                countryId: this.lia.liaContact.countryId,
            });
        }
        else if (this.lia.liaInvoiceAddress.typeOfAddressId == this.codes.address.business) {
            detail = null;
        }
        else {
            detail = null;
        }
        return of(detail).pipe(delay(0));
    }

    createAddressDropdownList(): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(this.lia.liaContact) });

        if (!this.onlyPrivateAddressAllowed) {

            for (let i = 0; i < this.lia.liaEmploymentB?.employmentRecords.length; i++) {
                // Use employmentB id as item value
                addresses.push({ value: this.lia.liaEmploymentB.employmentRecords[i].id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(this.lia.liaEmploymentB.employmentRecords[i]) });
            }

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: LiaContactDto) {
        let str = contact?.street;

        if (contact?.streetNr && contact.streetNr.length > 0) {
            str = str.concat(' ');
            str = str.concat(contact.streetNr);
        }

        str = str?.concat(', ');
        str = str?.concat(contact?.city);

        return str;
    }

    private getEmploymentAddressDescription(employment: LiaEmploymentBRecordDto) {
        let str = employment?.businessName;

        if (employment.street?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.street);

            if (employment.streetNr?.length > 0) {
                str = str.concat(' ');
                str = str.concat(employment.streetNr);
            }
        }

        if (employment.city?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.city);
        }

        return str;
    }



    createOrUpdateLiaInvoiceAddress(input: CreateOrUpdateLiaInvoiceAddressInput): Observable<void> {
        this.setStepToReview(LiaStatus.InvoiceAddress);
        return this.liaInvoiceAddressService.createOrUpdateLiaInvoiceAddress(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaInvoiceAddress = input.liaInvoiceAddress;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    //createAddressDropdownListForEhc(liaContact: LiaContactDto, liaEmployments: LiaEmploymentReadOnlyDto[]): SelectItem[] {

    //    var addresses: SelectItem[] = [];

    //    addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(liaContact) });
    //    for (let i = 0; i < liaEmployments.length; i++) {
    //        if (liaEmployments[i].isMarkedForEhc == true) {
    //            addresses.push({ value: liaEmployments[i].id ?? liaEmployments[i].guid, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(liaEmployments[i]) });
    //        }
    //    }

    //    addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });

    //    return addresses;
    //}


    checkDocuments(): Observable<LiaStatus[]> {
        return this.liaTypeService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }


    getUrlForStep(step: number): string {
        switch (step) {
            case LiaStatus.Person:
                return 'person';
            case LiaStatus.Contact:
                return 'contact';
            case LiaStatus.Restriction:
                return 'restriction';
            case LiaStatus.RestrictionB:
                return 'restriction-b';
            case LiaStatus.LanguageSkill:
                return 'language-skills';
            case LiaStatus.LanguageSkillB:
                return 'language-skills-b';
            case LiaStatus.EmploymentB:
                return 'employment-b';
            case LiaStatus.PreviousEmployment:
                return 'previous-employment';
            case LiaStatus.Guarantee:
                return 'guarantee';
            case LiaStatus.InvoiceAddress:
                return 'invoice-address';
            case LiaStatus.Document:
                return 'document';
            case LiaStatus.AcademicTitle:
                return 'academic-title';
            case LiaStatus.PostgraduateTitlePrivateLaw:
                return 'postgraduate-title-private-law';
            case LiaStatus.Diploma:
                return 'diploma';
            case LiaStatus.PostgraduateTitle:
                return 'postgraduate-title';
            case LiaStatus.LicenceInAnotherCanton:
                return 'licence-in-another-canton';
            case LiaStatus.ContactDuringEmployment:
                return 'contact-during-employment';
            case LiaStatus.ProfessionalResponsibility:
                return 'professional-responsibility';
            case LiaStatus.Release:
                return 'release';
            case LiaStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    isEligibleForEhc(): Observable<boolean> {
        return this.liaTypeService.isEligibleForEhc(this.lia.professionId);
    }

    isUniversitary(): Observable<boolean> {
        return this.liaTypeService.isUniversitary(this.lia.professionId);
    }
}
